/**
 * Plain-text auto-link parser used by ``AutoLinkText`` and the project
 * description display (A2F-733).
 *
 * Splits a string into alternating ``text`` and ``link`` segments. URLs
 * are detected by an ``https?://`` prefix; common trailing punctuation
 * (.,;:!?)]}>) is peeled off so a sentence-ending period after a URL
 * doesn't become part of the link.
 *
 * The parser is pure — it returns plain data, not React nodes — so it's
 * trivially unit-testable. The companion ``AutoLinkText`` component
 * consumes the segments and handles rendering (with React's automatic
 * HTML escaping doing the XSS heavy lifting).
 */

const URL_RE = /(https?:\/\/[^\s<]+)/g;
const TRAILING_PUNCT_RE = /[.,;:!?)\]}>]+$/;

export type AutoLinkSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; href: string; trailing: string };

export function parseAutoLinks(text: string): AutoLinkSegment[] {
  if (!text) return [];
  const segments: AutoLinkSegment[] = [];
  text.split(URL_RE).forEach((piece, idx) => {
    if (idx % 2 === 1) {
      // URL capture group — peel trailing punctuation
      const m = piece.match(TRAILING_PUNCT_RE);
      const href = m ? piece.slice(0, -m[0].length) : piece;
      segments.push({ type: 'link', href, trailing: m ? m[0] : '' });
    } else if (piece) {
      segments.push({ type: 'text', value: piece });
    }
  });
  return segments;
}
